import { closestCenter, pointerWithin } from '@dnd-kit/core'
import type { CollisionDetection } from '@dnd-kit/core'

/**
 * Collision detection for the lobby-day scheduler's shared DndContext.
 *
 * `closestCenter` — dnd-kit's default and what this context used to pass — ignores
 * `pointerCoordinates` entirely and ranks droppables by the distance between the *dragged
 * rect's centre* and each droppable's centre. On this lane that resolves to the wrong
 * target for large stretches of a drag:
 *
 *   - there is no DragOverlay for a meeting card, so the "dragged rect" is the card's own
 *     (stationary) rect translated by the drag delta — its centre starts on top of the
 *     card's own `meeting:<id>` droppable, which dnd-kit never excludes (the draggable is
 *     `card:<id>`, a different id, and only `disabled` droppables are filtered out);
 *   - the `slot:<time>` bands span the full lane width, so every band's centre sits at the
 *     lane's horizontal centre. A card in a right-hand overlap column is therefore
 *     hundreds of pixels from every band's centre while being zero pixels from its own.
 *
 * The result is a dead zone in which no band highlights and a drop silently does nothing —
 * "you may have to move the mouse around a bit before the rows will highlight".
 *
 * This detector resolves by the pointer instead, which is what the user is aiming with:
 *
 *   1. `pointerWithin` — the droppables the cursor is actually inside.
 *   2. Cards before bands. Cards paint on top of the full-lane bands, so when the cursor is
 *      inside both, the card is what the user sees under it. `pointerWithin` alone ranks by
 *      average distance to a rect's corners, which lets a short wide band beat a tall card.
 *   3. `closestCenter` as a fallback when the cursor is inside nothing (page chrome, the
 *      time gutter) or when there is no cursor at all (keyboard drags report none), so
 *      those drags keep resolving exactly as they did before.
 *
 * Step 2 alone is not enough, because a card's droppable stays registered at its original
 * (unmoved) layout position for the whole drag. See `candidatesFor` below.
 */

const isBand = (id: unknown) => String(id).startsWith('slot:')
const isCard = (id: unknown) => String(id).startsWith('meeting:')

/**
 * Narrow the droppable set to the targets the drag can actually act on.
 *
 * While a meeting card is being dragged, the only actionable target is a time band:
 * `handleDragEnd`'s `meeting-card` branch acts on `o.type === 'slot'` and returns without
 * doing anything for every other resolution. Cards do not move during a drag and keep their
 * `meeting:<id>` droppables registered at their original rects, so leaving them in the
 * candidate set turns every card's footprint into a dead zone: the card is elected, it paints
 * an affirmative `isOver` border, no band highlights, and the drop does nothing. A 60-minute
 * meeting is 120px tall, so nudging one down a slot never leaves its own footprint at all.
 *
 * This is deliberately the whole class, not just the dragged card — crossing a *different*
 * card (two legislators in adjacent overlap columns) goes dead by exactly the same mechanism.
 *
 * The other two drag types are left completely alone: a pool attendee can be dropped on a
 * meeting (assign) or a band (create), and an assignment chip on a meeting (move) or the pool
 * (unassign). For those, a card under the cursor is a real target and must keep winning.
 */
function candidatesFor(args: Parameters<CollisionDetection>[0]): Parameters<CollisionDetection>[0] {
  if (args.active.data.current?.type !== 'meeting-card') return args
  return { ...args, droppableContainers: args.droppableContainers.filter((c) => !isCard(c.id)) }
}

export const schedulerCollisionDetection: CollisionDetection = (args) => {
  const candidates = candidatesFor(args)
  const within = pointerWithin(candidates)
  if (within.length === 0) return closestCenter(candidates)
  return [...within.filter((c) => !isBand(c.id)), ...within.filter((c) => isBand(c.id))]
}
